import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { doc, setDoc } from "firebase/firestore";
import { db, auth } from "../config/firebase";

const randomCode  = () => String(100000 + (crypto.getRandomValues(new Uint32Array(1))[0] % 900000));
const randomToken = () =>
  Array.from(crypto.getRandomValues(new Uint8Array(24)), b => b.toString(16).padStart(2, "0")).join("");

const ORG_TYPES = [
  { id: "startup",   label: "Early-stage startup",    sub: "Pre-seed to Series A" },
  { id: "growth",    label: "Growth-stage company",   sub: "Series B+" },
  { id: "smb",       label: "Small business",         sub: "Established, profitable" },
  { id: "solo",      label: "Solo founder",           sub: "Just me for now" },
  { id: "nonprofit", label: "Nonprofit / NGO",        sub: "Mission-driven org" },
  { id: "agency",    label: "Agency / Studio",        sub: "Client-based revenue" },
];

const FUNDING_STAGES = [
  { id: "bootstrapped", label: "Bootstrapped" },
  { id: "pre_seed",     label: "Pre-seed" },
  { id: "seed",         label: "Seed" },
  { id: "series_a",     label: "Series A" },
  { id: "series_b",     label: "Series B+" },
  { id: "profitable",   label: "Profitable / no funding" },
];

const REVENUE_MODELS = [
  { id: "saas",        label: "SaaS / subscription" },
  { id: "services",    label: "Services / consulting" },
  { id: "ecommerce",   label: "E-commerce / product" },
  { id: "marketplace", label: "Marketplace" },
  { id: "pre_revenue", label: "Pre-revenue" },
  { id: "other",       label: "Other" },
];

const DEFAULT_DEPTS = ["Engineering", "Marketing", "Sales", "Operations", "Design", "Finance", "HR", "Product"];

function buildConfig(answers) {
  const { cashBalance, monthlyBurn, mrr, selectedDepts, customDepts } = answers;
  const cash     = Number(cashBalance) || 0;
  const burn     = Number(monthlyBurn) || 0;
  const mrrNum   = Number(mrr) || 0;
  const netBurn  = Math.max(burn - mrrNum, 0);
  const runwayMonths = netBurn > 0 && cash > 0 ? Math.floor(cash / netBurn) : null;
  const monthlyBudget = burn || 0;
  // Max monthly spend to sustain 18 months of runway
  const sustainableBudget = cash > 0 ? Math.floor(cash / 18) + mrrNum : 0;
  const allDepts = [...selectedDepts, ...customDepts.filter(d => d.trim())];

  const categories = [];
  const add = (name, limit = 0) => categories.push({ id: Date.now() + Math.random(), name, limit });

  allDepts.forEach(dept => {
    const pct = allDepts.length > 0 ? 1 / allDepts.length : 0;
    add(dept, monthlyBudget > 0 ? Math.round(monthlyBudget * pct) : 0);
  });

  if (allDepts.length === 0) {
    add("Payroll",            monthlyBudget > 0 ? Math.round(monthlyBudget * 0.55) : 0);
    add("Infrastructure",     monthlyBudget > 0 ? Math.round(monthlyBudget * 0.10) : 0);
    add("Marketing & Ads",    monthlyBudget > 0 ? Math.round(monthlyBudget * 0.12) : 0);
    add("SaaS Tools",         monthlyBudget > 0 ? Math.round(monthlyBudget * 0.05) : 0);
    add("Travel & Offsites",  monthlyBudget > 0 ? Math.round(monthlyBudget * 0.04) : 0);
    add("Legal & Compliance", monthlyBudget > 0 ? Math.round(monthlyBudget * 0.04) : 0);
    add("Miscellaneous",      monthlyBudget > 0 ? Math.round(monthlyBudget * 0.10) : 0);
  } else {
    add("SaaS Tools",         monthlyBudget > 0 ? Math.round(monthlyBudget * 0.04) : 0);
    add("Travel & Offsites",  monthlyBudget > 0 ? Math.round(monthlyBudget * 0.03) : 0);
    add("Legal & Compliance", monthlyBudget > 0 ? Math.round(monthlyBudget * 0.03) : 0);
  }

  return { monthlyBudget, categories, runwayMonths, cash, netBurn, sustainableBudget, mrrNum };
}

const optBtn = (active) => ({
  background: "transparent",
  border: `1px solid ${active ? "var(--green)" : "var(--border)"}`,
  borderRadius: "var(--radius)",
  padding: "8px 10px",
  color: active ? "var(--green)" : "var(--text-muted)",
  cursor: "pointer",
  fontSize: 12,
  fontFamily: "var(--sans)",
  textAlign: "left",
  transition: "all 0.15s",
  width: "100%",
});

export default function SetupPage({ uid, onComplete }) {
  const navigate = useNavigate();
  const [step, setStep]     = useState(0);
  const [saving, setSaving] = useState(false);

  const [answers, setAnswers] = useState({
    orgType:       null,
    orgName:       "",
    fundingStage:  null,
    revenueModel:  null,
    cashBalance:   "",
    monthlyBurn:   "",
    mrr:           "",
    selectedDepts: [],
    customDepts:   ["", ""],
    teamSize:      "",
  });

  const set = (key, val) => setAnswers(a => ({ ...a, [key]: val }));
  const toggleDept = (name) =>
    set("selectedDepts", answers.selectedDepts.includes(name)
      ? answers.selectedDepts.filter(d => d !== name)
      : [...answers.selectedDepts, name]
    );

  const { runwayMonths, monthlyBudget, categories, netBurn, cash, sustainableBudget, mrrNum } = buildConfig(answers);
  const runwayColor = runwayMonths == null
    ? "var(--text-muted)"
    : runwayMonths <= 3  ? "var(--red)"
    : runwayMonths <= 6  ? "var(--amber)"
    : "var(--green)";

  const STEPS = [
    {
      title: "About your organisation",
      subtitle: "We'll use this to tailor your setup.",
      content: (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <div className="label" style={{ marginBottom: 5 }}>company / project name</div>
            <input
              placeholder="e.g. Acme Inc."
              value={answers.orgName}
              onChange={e => set("orgName", e.target.value)}
            />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginTop: 4 }}>
            {ORG_TYPES.map(o => (
              <button key={o.id} style={optBtn(answers.orgType === o.id)} onClick={() => set("orgType", o.id)}>
                <div style={{ fontSize: 12 }}>{o.label}</div>
                <div style={{ fontSize: 10, color: answers.orgType === o.id ? "#86efac" : "var(--text-dim)", marginTop: 2 }}>{o.sub}</div>
              </button>
            ))}
          </div>
        </div>
      ),
    },
    {
      title: "Funding & revenue",
      subtitle: "Helps us set the right financial context.",
      content: (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <div className="label" style={{ marginBottom: 6 }}>funding stage</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 5 }}>
              {FUNDING_STAGES.map(f => (
                <button key={f.id} style={{ ...optBtn(answers.fundingStage === f.id), textAlign: "center" }}
                  onClick={() => set("fundingStage", f.id)}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="label" style={{ marginBottom: 6 }}>revenue model</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5 }}>
              {REVENUE_MODELS.map(r => (
                <button key={r.id} style={optBtn(answers.revenueModel === r.id)}
                  onClick={() => set("revenueModel", r.id)}>
                  {r.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Your numbers",
      subtitle: "We'll calculate your monthly budget and runway automatically.",
      content: (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {[
            { label: "cash balance ($)",                    key: "cashBalance",  ph: "e.g. 500000",  hint: "total cash in bank right now" },
            { label: "monthly burn rate ($)",               key: "monthlyBurn",  ph: "e.g. 45000",   hint: "total monthly spend" },
            { label: "mrr — monthly recurring revenue ($)", key: "mrr",          ph: "e.g. 12000",   hint: "leave blank if pre-revenue" },
          ].map(({ label, key, ph, hint }) => (
            <div key={key}>
              <div className="label" style={{ marginBottom: 5 }}>{label}</div>
              <input type="number" placeholder={ph} value={answers[key]} onChange={e => set(key, e.target.value)} />
              <div className="mono" style={{ fontSize: 10, color: "var(--text-dim)", marginTop: 4 }}>{hint}</div>
            </div>
          ))}

          {(answers.cashBalance || answers.monthlyBurn) && (
            <div className="card" style={{ marginTop: 4, borderColor: runwayColor + "33", display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ display: "flex", gap: 24 }}>
                <div>
                  <div className="label" style={{ marginBottom: 3 }}>monthly_budget</div>
                  <span className="mono" style={{ fontSize: 14, color: "var(--green)" }}>
                    {monthlyBudget > 0 ? `$${monthlyBudget.toLocaleString()}/mo` : "—"}
                  </span>
                </div>
                <div>
                  <div className="label" style={{ marginBottom: 3 }}>runway</div>
                  <span className="mono" style={{ fontSize: 14, color: runwayColor }}>
                    {runwayMonths != null ? `${runwayMonths} months` : "—"}
                  </span>
                </div>
              </div>
              {runwayMonths != null && runwayMonths < 18 && sustainableBudget > 0 && sustainableBudget < monthlyBudget && (
                <div style={{ paddingTop: 8, borderTop: "1px solid #111" }}>
                  <span className="mono" style={{ fontSize: 10, color: "var(--amber)" }}>
                    to reach 18mo runway, cut spend to ${sustainableBudget.toLocaleString()}/mo
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Departments",
      subtitle: "Select the teams in your org. We'll create a budget category for each.",
      content: (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5 }}>
            {DEFAULT_DEPTS.map(name => (
              <button key={name} style={optBtn(answers.selectedDepts.includes(name))}
                onClick={() => toggleDept(name)}>
                {answers.selectedDepts.includes(name) ? "✓  " : ""}{name}
              </button>
            ))}
          </div>
          <div className="label" style={{ marginTop: 4 }}>custom (optional)</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {answers.customDepts.map((val, i) => (
              <input key={i} placeholder={`custom department ${i + 1}`} value={val}
                onChange={e => {
                  const updated = [...answers.customDepts];
                  updated[i] = e.target.value;
                  set("customDepts", updated);
                }} />
            ))}
          </div>
        </div>
      ),
    },
    {
      title: "Team size",
      subtitle: "Helps calibrate per-head costs in the Departments view.",
      content: (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {[
            { id: "1",     label: "Just me" },
            { id: "2-5",   label: "2 – 5 people" },
            { id: "6-15",  label: "6 – 15 people" },
            { id: "16-50", label: "16 – 50 people" },
            { id: "51+",   label: "51+ people" },
          ].map(({ id, label }) => (
            <button key={id} style={optBtn(answers.teamSize === id)} onClick={() => set("teamSize", id)}>
              {label}
            </button>
          ))}
        </div>
      ),
    },
    {
      title: "Ready to launch",
      subtitle: "Here's what we're setting up for you.",
      content: (() => {
        const allDepts = [...answers.selectedDepts, ...answers.customDepts.filter(d => d.trim())];
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
              {[
                { label: "monthly_budget", value: monthlyBudget > 0 ? `$${monthlyBudget.toLocaleString()}` : "not set", color: "var(--green)" },
                { label: "runway",         value: runwayMonths != null ? `${runwayMonths} mo` : "not set",              color: runwayColor },
                { label: "departments",    value: allDepts.length > 0 ? String(allDepts.length) : "default",           color: "var(--blue)" },
                { label: "team_size",      value: answers.teamSize || "not set",                                        color: "var(--purple)" },
              ].map(({ label, value, color }) => (
                <div key={label} className="card" style={{ borderLeft: `2px solid ${color}` }}>
                  <div className="label" style={{ marginBottom: 4 }}>{label}</div>
                  <span className="mono" style={{ fontSize: 14, color }}>{value}</span>
                </div>
              ))}
            </div>

            <div className="label" style={{ marginTop: 4 }}>categories ({categories.length})</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, maxHeight: 160, overflowY: "auto" }}>
              {categories.map(c => (
                <div key={c.id} className="card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 12, color: "var(--text)" }}>{c.name}</span>
                  <span className="mono" style={{ fontSize: 10, color: "var(--text-muted)" }}>
                    {c.limit > 0 ? `$${c.limit.toLocaleString()}` : "—"}
                  </span>
                </div>
              ))}
            </div>
            <div className="mono" style={{ fontSize: 10, color: "var(--text-dim)" }}>everything is editable after setup</div>
          </div>
        );
      })(),
    },
  ];

  const isLast = step === STEPS.length - 1;

  const handleFinish = async () => {
    setSaving(true);
    const { monthlyBudget, categories } = buildConfig(answers);
    const workspaceId = `ws_${uid}`;
    const budgetData = {
      monthlyBudget,
      categories: categories.map(c => ({ ...c, id: Date.now() + Math.floor(Math.random() * 99999) })),
      transactions: [],
      onboarded: true,
      runwaySettings: {
        cashBalance:           String(Number(answers.cashBalance) || 0),
        mrr:                   answers.mrr,
        monthlyBurnOverride:   String(Number(answers.monthlyBurn) || 0),
        monthsToClose:         "6",
        targetPostRaiseRunway: "18",
        momGrowthRate:         "",
        mrrHistory:            {},
      },
      meta: {
        orgName:      answers.orgName,
        orgType:      answers.orgType,
        fundingStage: answers.fundingStage,
        revenueModel: answers.revenueModel,
        teamSize:     answers.teamSize,
        cashBalance:  Number(answers.cashBalance) || 0,
        monthlyBurn:  Number(answers.monthlyBurn) || 0,
        mrr:          Number(answers.mrr) || 0,
      },
    };

    await setDoc(doc(db, "workspaces", workspaceId), {
      name: answers.orgName || "My Workspace",
      ownerId: uid,
      memberUids: [uid],
      inviteToken: randomToken(),
      inviteCode: randomCode(),
      members: [{
        uid,
        email: auth.currentUser?.email ?? "",
        name: auth.currentUser?.displayName ?? "Owner",
        role: "admin",
        status: "active",
      }],
      createdAt: new Date().toISOString(),
    });

    await setDoc(doc(db, "workspaces", workspaceId, "budget", "data"), budgetData);

    await setDoc(doc(db, "users", uid), {
      workspaceId,
      role: "admin",
      onboarded: true,
    }, { merge: true });

    onComplete();
    navigate("/start-trial");
  };

  const handleSkipAll = async () => {
    setSaving(true);
    const workspaceId = `ws_${uid}`;

    await setDoc(doc(db, "workspaces", workspaceId), {
      name: "My Workspace",
      ownerId: uid,
      memberUids: [uid],
      inviteToken: randomToken(),
      inviteCode: randomCode(),
      members: [{
        uid,
        email: auth.currentUser?.email ?? "",
        name: auth.currentUser?.displayName ?? "Owner",
        role: "admin",
        status: "active",
      }],
      createdAt: new Date().toISOString(),
    });

    await setDoc(doc(db, "workspaces", workspaceId, "budget", "data"), {
      monthlyBudget: 0,
      categories: [
        { id: 1, name: "Payroll",         limit: 0 },
        { id: 2, name: "Infrastructure",  limit: 0 },
        { id: 3, name: "Marketing",       limit: 0 },
        { id: 4, name: "Miscellaneous",   limit: 0 },
      ],
      transactions: [],
      onboarded: true,
      runwaySettings: {
        cashBalance: "", mrr: "", monthlyBurnOverride: "",
        monthsToClose: "6", targetPostRaiseRunway: "18",
        momGrowthRate: "", mrrHistory: {},
      },
    });

    await setDoc(doc(db, "users", uid), {
      workspaceId,
      role: "admin",
      onboarded: true,
    }, { merge: true });

    onComplete();
    navigate("/start-trial");
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "var(--bg)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 24,
    }}>
      <div style={{ width: "100%", maxWidth: 500 }}>

        {/* Logo */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 36 }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--green)" }} />
          <span style={{ fontFamily: "var(--mono)", fontSize: 13, color: "var(--text)" }}>greenline</span>
        </div>

        {/* Progress bar */}
        <div style={{ display: "flex", gap: 4, marginBottom: 24 }}>
          {STEPS.map((_, i) => (
            <div key={i} style={{
              flex: 1, height: 2, borderRadius: 1,
              background: i < step ? "var(--green)" : i === step ? "var(--green-mid)" : "var(--border)",
              transition: "background 0.3s",
            }} />
          ))}
        </div>

        {/* Step label */}
        <div className="label" style={{ marginBottom: 16 }}>
          step_{step + 1}_of_{STEPS.length}
        </div>

        {/* Card */}
        <div className="card" style={{ padding: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text)", marginBottom: 4 }}>
            {STEPS[step].title}
          </div>
          <div className="mono" style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 20, lineHeight: 1.5 }}>
            {STEPS[step].subtitle}
          </div>

          {STEPS[step].content}

          {/* Actions */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 20 }}>
            {step > 0 && (
              <button className="btn" onClick={() => setStep(s => s - 1)}>
                ← back
              </button>
            )}
            <div style={{ flex: 1 }} />
            {!isLast && (
              <button
                onClick={() => setStep(s => s + 1)}
                style={{ background: "none", border: "none", fontFamily: "var(--mono)", fontSize: 10, color: "var(--text-dim)", cursor: "pointer", padding: "4px 8px" }}
              >
                skip
              </button>
            )}
            <button
              className="btn btn-green"
              onClick={isLast ? handleFinish : () => setStep(s => s + 1)}
              disabled={saving}
              style={{ fontSize: 12 }}
            >
              {saving ? "setting up…" : isLast ? "launch greenline →" : "continue →"}
            </button>
          </div>
        </div>

        {!isLast && (
          <div style={{ textAlign: "center", marginTop: 16 }}>
            <button
              onClick={handleSkipAll}
              disabled={saving}
              style={{ background: "none", border: "none", fontFamily: "var(--mono)", fontSize: 10, color: "var(--text-dim)", cursor: "pointer" }}
            >
              skip setup entirely →
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
